<!-- The sounds controls (src/stores/sounds.ts): on / off, background music, the music and effects volumes. In
     Settings › Appearance (for every theme) and in the speaker button's panel (SoundButton.vue, only with a theme other
     than Default). Default has no music: its checkbox is off and says why. Stored on this device (creatorOS_sounds). -->
<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from '../composables/useI18n'
import { useSoundsStore } from '../stores/sounds'
import { useThemeStore } from '../stores/theme'
import { musicOf, playSound } from '../features/sounds'
import { chipCls } from '../utils/chip'

withDefaults(defineProps<{ compact?: boolean }>(), { compact: false })
const { t } = useI18n()
const sounds = useSoundsStore()
const theme = useThemeStore()
sounds.load()
const s = sounds.settings
const hasMusic = computed(() => !!musicOf(theme.style))
const pill = (on: boolean) => chipCls(on, 'field', 'px-3.5 py-1.5')
const num = (e: Event) => Number((e.target as HTMLInputElement).value)
</script>

<template>
  <div class="sound-panel space-y-3">
    <div class="space-y-2">
      <div v-if="!compact"><div class="text-sm font-semibold text-gray-200">{{ t('Sounds') }}</div><div class="text-[11px] text-gray-500">{{ t('Small sounds when you click, open a window or finish something. Off unless you turn them on.') }}</div></div>
      <div v-else class="text-xs font-bold text-gray-400 tracking-wide">{{ t('Sounds and music') }}</div>
      <div class="flex flex-wrap gap-2">
        <button type="button" :class="pill(s.muted)" :aria-pressed="s.muted" @click="s.muted || sounds.toggleMute()">{{ t('Sounds off') }}</button>
        <button type="button" :class="pill(!s.muted)" :aria-pressed="!s.muted" @click="s.muted && sounds.toggleMute()">{{ t('Sounds on') }}</button>
      </div>
    </div>
    <label :class="['flex items-start gap-2.5 text-sm', hasMusic ? 'cursor-pointer text-gray-200' : 'opacity-60 text-gray-400']">
      <input type="checkbox" class="mt-0.5 accent-[rgb(var(--brand))]" :checked="s.musicOn && hasMusic" :disabled="!hasMusic"
        @change="sounds.set({ musicOn: ($event.target as HTMLInputElement).checked })">
      <span>
        <span class="block font-semibold">{{ t('Background music') }}</span>
        <span v-if="!compact" class="block text-[11px] text-gray-500">{{ hasMusic ? t('Soft music for the theme. It starts after your first click and goes quiet during the teleprompter.') : t('The music comes with the themes: choose one above.') }}</span>
      </span>
    </label>
    <div class="grid grid-cols-[4.5rem_1fr] items-center gap-x-3 gap-y-2 text-xs text-gray-400">
      <span>{{ t('Music') }}</span>
      <input type="range" min="0" max="1" step="0.05" class="sound-range" :value="s.music" :disabled="!hasMusic" :aria-label="t('Music')" @input="sounds.set({ music: num($event) })">
      <span>{{ t('Effects') }}</span>
      <input type="range" min="0" max="1" step="0.05" class="sound-range" :value="s.effects" :aria-label="t('Effects')" @input="sounds.set({ effects: num($event) })" @change="playSound('click')">
    </div>
  </div>
</template>
